/**
 * @voltxampere/web — Command palette registry (Sprint 22 C1)
 *
 * Singleton {@link CommandRegistry} keyed by `Command.id`. The
 * boot path (Sprint 22 C3) feeds it ~100 builtin commands; the
 * palette UI (Sprint 22 C2) consumes the registry through `search`
 * and `execute`. No I/O lives here — recent-commands persistence
 * is the C4 layer's job.
 *
 * Design notes:
 *
 *  - **Idempotent boot, fail-loud reuse.** `register()` throws on
 *    duplicate ids (DuplicateCommandIdError) so an accidental
 *    double-register during HMR is loud, not silent. Tests can
 *    call `__resetRegistryForTests()` between cases.
 *
 *  - **Teardown closure.** `register()` returns a function that
 *    removes the command — useful for transient commands tied to
 *    a UI mode (e.g. tutorial steps that vanish when the tour
 *    ends). The C3 builtin batch never calls its teardown.
 *
 *  - **`when()` is consulted twice:** during `search()` (filters
 *    out unavailable commands) and during `execute()` (returns
 *    silently rather than throwing — the user could have changed
 *    state between selecting and pressing Enter).
 *
 *  - **Stable sort.** `Array.prototype.sort` is stable in modern
 *    JS engines, so two commands with the same fuzzy score keep
 *    their insertion order. Builtin batches register in
 *    deliberate order (file > edit > view…) so ties surface the
 *    canonical category first.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { fuzzyScore } from "@voltxampere/shared";

import {
  type Command,
  type CommandSearchOptions,
  type CommandSearchResult,
  DuplicateCommandIdError,
  UnknownCommandIdError,
} from "./types.js";

const DEFAULT_SEARCH_LIMIT = 50;

export class CommandRegistry {
  private readonly commands = new Map<string, Command>();

  /**
   * Register a new command. Throws {@link DuplicateCommandIdError}
   * when `command.id` is already present. Returns a teardown
   * closure that removes the command — calling it twice is a
   * no-op.
   */
  register(command: Command): () => void {
    if (this.commands.has(command.id)) {
      throw new DuplicateCommandIdError(command.id);
    }
    this.commands.set(command.id, command);
    return () => {
      this.commands.delete(command.id);
    };
  }

  /**
   * Remove a command by id. Returns `true` when the id was
   * present, `false` otherwise. The teardown closure returned by
   * `register()` is preferred for ephemeral commands; this method
   * exists for symmetry and for the developer-tools panel that
   * may need to wipe registrations imperatively.
   */
  unregister(id: string): boolean {
    return this.commands.delete(id);
  }

  /** Resolve a command by id, or `undefined` if absent. */
  getById(id: string): Command | undefined {
    return this.commands.get(id);
  }

  /**
   * Snapshot of all registered commands in insertion order. The
   * returned array is fresh — mutating it does not affect the
   * registry.
   */
  list(): readonly Command[] {
    return Array.from(this.commands.values());
  }

  /** Total registered command count. */
  size(): number {
    return this.commands.size;
  }

  /**
   * Run a command by id. Resolves to:
   *
   *  - The command's return value (or `await` of its Promise) on
   *    success.
   *  - `undefined` if the command's `when()` gate returns false.
   *
   * Throws {@link UnknownCommandIdError} for missing ids — the
   * palette UI should never construct an id that wasn't returned
   * by `search()`/`list()`, so an unknown id signals a bug.
   */
  async execute(id: string): Promise<void> {
    const cmd = this.commands.get(id);
    if (!cmd) {
      throw new UnknownCommandIdError(id);
    }
    if (cmd.when && !cmd.when()) {
      return;
    }
    await cmd.execute();
  }

  /**
   * Fuzzy-rank registered commands against `query`. Returns rows
   * with score > 0, sorted descending. `when()`-gated commands
   * are filtered out before scoring.
   *
   * Empty query → returns `[]`. The palette UI is expected to
   * display recent + suggested commands via `list()` in that case
   * (BLUEPRINT §14.4 empty-query behaviour).
   */
  search(query: string, options: CommandSearchOptions = {}): readonly CommandSearchResult[] {
    if (query.length === 0) return [];
    const resolveKey = options.resolveKey ?? identity;
    const limit = options.limit ?? DEFAULT_SEARCH_LIMIT;

    const results: CommandSearchResult[] = [];
    for (const cmd of this.commands.values()) {
      if (cmd.when && !cmd.when()) continue;
      // Sprint 25 R2 lane 6 — score the localized title AND the
      // command id, then keep the higher-scoring match. This rescues
      // queries that target the canonical id (e.g. "voltage") when
      // the localized title is in a different language and would
      // otherwise mis-match (TR "Voltaj bölücü" lacks the "g" of
      // "voltage", scoring 0). Highlight indices come from whichever
      // surface won so the row's visible glyph highlight stays
      // accurate. Description fuzzy is deferred to Sprint 25 R3.
      const title = resolveKey(cmd.titleKey);
      const titleMatch = fuzzyScore(query, title);
      const idMatch = fuzzyScore(query, cmd.id);
      const score = Math.max(titleMatch.score, idMatch.score);
      if (score === 0) continue;
      // Highlights always come from the title — that is the visible
      // text in the palette row. If only the id matched (the title
      // is in another locale and lacks the query character set), the
      // row still surfaces but with no glyph highlight.
      const matchedIndices = titleMatch.score > 0 ? titleMatch.matchedIndices : [];
      results.push({ command: cmd, score, matchedIndices });
    }
    results.sort((a, b) => b.score - a.score);
    return limit > 0 && results.length > limit ? results.slice(0, limit) : results;
  }

  /**
   * Test-only helper: drop every registered command. Production
   * code should never call this; the boot path registers exactly
   * once and the palette UI consumes the registry for the lifetime
   * of the page.
   */
  __resetForTests(): void {
    this.commands.clear();
  }
}

function identity(s: string): string {
  return s;
}

/**
 * Process-wide singleton consumed by `apps/web/src/main.ts` boot
 * and the palette component. Tests instantiate fresh registries
 * via `new CommandRegistry()` so the singleton stays clean.
 */
export const commandRegistry = new CommandRegistry();
